import { useEffect, useMemo, useRef, useState } from 'react';
import type { ProductIndexEntry } from '@/shared/bfSeller';
import type { ProductProps } from '@/types';

/**
 * Rolagem infinita sobre o índice leve.
 *
 * Cada avanço de página abre só o trecho novo. O que já foi aberto fica num mapa desta tela —
 * separado do cache compartilhado de propósito: o cache tem teto e descarta os mais antigos, e
 * sem esse mapa a lista releria do banco, a cada página, tudo o que o teto tivesse descartado.
 *
 * A divisão é: a tela é dona do que está exibindo; o cache compartilhado serve ao reaproveitamento
 * entre telas e é ele que responde pelo limite de memória.
 */
export const useProductPagination = ({
  entries,
  page,
  pageSize,
  hydrate,
  cacheKey,
  enabled = true,
}: {
  entries: ProductIndexEntry[];
  page: number;
  pageSize: number;
  hydrate: (entries: ProductIndexEntry[]) => Promise<ProductProps[]>;
  /** Muda quando o conjunto/escopo muda e o que a tela abriu deixa de valer. */
  cacheKey: string;
  enabled?: boolean;
}) => {
  const abertos = useRef(new Map<number, ProductProps>());
  const lastCacheKey = useRef(cacheKey);
  const [items, setItems] = useState<ProductProps[]>([]);
  const [isLoadingPage, setIsLoadingPage] = useState(false);

  if (lastCacheKey.current !== cacheKey) {
    lastCacheKey.current = cacheKey;
    abertos.current = new Map();
  }

  const visible = useMemo(() => entries.slice(0, Math.max(1, page) * pageSize), [entries, page, pageSize]);
  const total = entries.length;
  const hasMore = visible.length < total;

  // Assinatura da fatia visível: evita reprocessar quando o array é recriado com o mesmo conteúdo.
  const visibleSignature = useMemo(
    () => `${cacheKey}|${visible.length}|${visible[0]?.cod ?? ''}|${visible[visible.length - 1]?.cod ?? ''}`,
    [cacheKey, visible]
  );

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      return;
    }

    let cancelled = false;

    const publicar = () => {
      if (cancelled) return;
      const out: ProductProps[] = [];
      for (const entry of visible) {
        const produto = abertos.current.get(entry.cod);
        // Produto restrito ou fora do escopo não volta da hidratação; sai da lista.
        if (produto) out.push(produto);
      }
      setItems(out);
    };

    const faltando = visible.filter((entry) => !abertos.current.has(entry.cod));
    if (!faltando.length) {
      publicar();
      return;
    }

    setIsLoadingPage(true);
    hydrate(faltando)
      .then((produtos) => {
        if (cancelled) return;
        for (const produto of produtos) abertos.current.set(Number(produto.COD_PRODUTO), produto);
        publicar();
      })
      .catch(() => publicar())
      .finally(() => {
        if (!cancelled) setIsLoadingPage(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visibleSignature, enabled, hydrate]);

  return { items, total, hasMore, isLoadingPage };
};
